import React, { useState, useEffect, useCallback } from "react";
import { HelpItemPayload } from "../../types/HelpTypes";
import TextField from "../../../events/components/EventForm/TextField";
import TextareaField from "../../../events/components/EventForm/TextareaField";
import SelectField from "../../../events/components/EventForm/SelectField";
import UploadField from "../UploadField";
import styles from "./index.module.scss";

export type HelpType = "PDF" | "Video";

interface HelpFormProps {
  onAdd?: (item: HelpItemPayload) => void;
  onSave?: (item: HelpItemPayload) => void;
  onDelete?: () => void;
  isEditing?: boolean;
  initialData?: Partial<HelpItemPayload>;
  onFormChange?: () => void;
  onValidationChange?: (isValid: boolean) => void;
}

const HelpForm: React.FC<HelpFormProps> = ({ onAdd, onSave, onDelete, isEditing = false, initialData, onFormChange, onValidationChange }) => {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [type, setType] = useState<HelpType>("PDF");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);

  // Utility function to extract filename from Azure Blob Storage URL
  const extractFileNameFromUrl = (url: string): string => {
    try {
      const urlPath = new URL(url).pathname;
      const fullFileName = urlPath.split('/').pop() || '';
      // Remove file extension for display
      const nameWithoutExtension = fullFileName.replace(/\.[^/.]+$/, '');
      return nameWithoutExtension;
    } catch {
      return 'Unknown file';
    }
  };

  // Initialize form with existing data when editing
  useEffect(() => {
    if (isEditing && initialData) {
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setType((initialData.type as HelpType) || "PDF");
      
      // Handle existing file URL
      if (initialData.url) {
        setExistingFileUrl(initialData.url);
        setExistingFileName(extractFileNameFromUrl(initialData.url));
      } else {
        setExistingFileUrl(null);
        setExistingFileName(null);
      }
    }
  }, [isEditing, initialData]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setType("PDF");
    setPdfFile(null);
    setVideoFile(null);
    setExistingFileUrl(null);
    setExistingFileName(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();
    
    if (!isFormValid()) return;

    const payload: HelpItemPayload = {
      title: trimmedTitle,
      description: trimmedDesc,
      type,
      url: undefined, // Files will be uploaded to Azure and URL will be set by the service
      fileName: type === "PDF" ? pdfFile?.name : videoFile?.name,
      file: type === "PDF" ? pdfFile || undefined : videoFile || undefined
    };

    if (isEditing) {
      onSave && onSave(payload);
    } else {
      onAdd && onAdd(payload);
      resetForm();
    }
  };

  const isFormValid = useCallback((): boolean => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return false;
    
    // For new items, require file selection
    if (!isEditing) {
      if (type === "PDF" && !pdfFile) return false;
      if (type === "Video" && !videoFile) return false;
    } else {
      // For editing, allow if there's either a new file or existing file
      if (type === "PDF" && !pdfFile && !existingFileUrl) return false;
      if (type === "Video" && !videoFile && !existingFileUrl) return false;
    }
    
    return true;
  }, [title, type, pdfFile, videoFile, isEditing, existingFileUrl]);

  // Update validation whenever form state changes
  useEffect(() => {
    onValidationChange && onValidationChange(isFormValid());
  }, [isFormValid, onValidationChange]);

  const handleInputChange = (setter: (value: any) => void, value: any) => {
    setter(value);
    onFormChange && onFormChange();
  };

  const handleRemoveExistingFile = () => {
    setExistingFileUrl(null);
    setExistingFileName(null);
    onFormChange && onFormChange();
  };



  return (
    <form onSubmit={handleSubmit} className={styles.formContainer}>
      <div className="row">
        <div className="col-12 col-md-9">
          <TextField
            name="title"
            inputValue={title}
            inputLabel="Title"
            placeholder="Enter a title"
            onChange={(_, v) => handleInputChange(setTitle, v)}
            isBold
          />
        </div>
        <div className="col-12 col-md-3">
          <SelectField
            label="Type"
            options={["PDF", "Video"]}
            value={type}
            name="type"
            onChange={(_, v) => handleInputChange(setType, v as HelpType)}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className={styles.descriptionSmall}>
            <TextareaField
              name="description"
              value={description}
              label="Description"
              placeholder="Add a description"
              onChange={(_, v) => handleInputChange(setDescription, v)}
            />
          </div>
        </div>
      </div>

      {type === "PDF" ? (
        <div className="row">
          <div className="col-12 mt-3">
            <UploadField
              label="Document"
              acceptedFileTypes={[
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "image/*"
              ]}
              fileSelected={pdfFile}
              existingFileName={existingFileName}
              existingFileUrl={existingFileUrl}
              onRemoveExisting={handleRemoveExistingFile}
              onChange={(f) => handleInputChange(setPdfFile, f)}
            />
          </div>
        </div>
      ) : (
        <div className="row mt-4">
          <div className="col-12">
            <UploadField
              label="Video"
              acceptedFileTypes={["video/*"]}
              fileSelected={videoFile}
              existingFileName={existingFileName}
              existingFileUrl={existingFileUrl}
              onRemoveExisting={handleRemoveExistingFile}
              onChange={(f) => handleInputChange(setVideoFile, f)}
            />
          </div>
        </div>
      )}

    </form>
  );
};

export default HelpForm;